import React from 'react';
import { Button } from '@mui/material';
import { FileDownload, FilterAlt } from '@mui/icons-material';
import { Spinner } from 'react-bootstrap';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import CustomIconButton from '../../../elements/CustomIconButton';
import LinkedProjectSelector from '../../../../features/jira/LinkedProjectSelector';

const ExportButtonAndFilters = ({
  exporting,
  setExporting,
  currentProject,
  selectedJiraProject,
  linkedJiraProjects,
  isJiraIntegrated,
  currentView,
  setIsFilterOpen,
  handleJiraProjectChange,
  pageRef,
  sectionRefs,
}) => {
  const margin = 10;
  const pdfPageWidth = 210;
  const fixedPageHeight = 297;
  const pxPerMm = 3.7795;

  const fixedHeightSections = ['test-automation'];

  const addSectionToPDF = async (element, sectionKey, isFirstPage = false) => {
    const canvas = await html2canvas(element, {
      backgroundColor: '#fff',
      useCORS: true,
      scale: 2,
    });

    const imgData = canvas.toDataURL('image/png');

    const imgWidthPx = canvas.width;
    const imgHeightPx = canvas.height;

    const imgWidthMM = imgWidthPx / pxPerMm;
    const imgHeightMM = imgHeightPx / pxPerMm;

    const maxWidthMM = pdfPageWidth - margin * 2;
    const scaleFactor = maxWidthMM / imgWidthMM;

    const scaledWidthMM = imgWidthMM * scaleFactor;
    const scaledHeightMM = imgHeightMM * scaleFactor;

    const isFixed = fixedHeightSections.includes(sectionKey);
    const pageHeightMM = isFixed
      ? fixedPageHeight
      : scaledHeightMM + margin * 2;

    const pdf = ExportButtonAndFilters.pdfInstance;

    if (isFirstPage) {
      pdf.internal.pageSize.setWidth(pdfPageWidth);
      pdf.internal.pageSize.setHeight(pageHeightMM);
      pdf.setPage(1);
    } else {
      pdf.addPage([pdfPageWidth, pageHeightMM]);
    }

    const yOffset = isFixed
      ? Math.max((fixedPageHeight - scaledHeightMM) / 2, margin)
      : margin;

    pdf.addImage(
      imgData,
      'PNG',
      margin,
      yOffset,
      scaledWidthMM,
      scaledHeightMM
    );
  };

  const downloadPDF = async () => {
    setExporting(true);

    try {
      ExportButtonAndFilters.pdfInstance = new jsPDF('p', 'mm');
      const pdf = ExportButtonAndFilters.pdfInstance;

      if (currentView) {
        const el = sectionRefs.current[currentView];
        if (el) {
          await addSectionToPDF(el, currentView, true);
        }
      } else {
        const allSections = Object.keys(sectionRefs.current);
        let isFirst = true;

        for (const key of allSections) {
          const el = sectionRefs.current[key];
          if (el && el.style.display !== 'none') {
            await addSectionToPDF(el, key, isFirst);
            isFirst = false;
          }
        }
      }

      const submenuName = currentView
        ? currentView.replace(/-/g, '_')
        : 'main';
      pdf.save(`${currentProject?.name}_${submenuName}.pdf`);
    } catch (e) {
      console.error('PDF export failed:', e);
    } finally {
      setExporting(false);
    }
  };

  const showJiraSelector =
    isJiraIntegrated &&
    (!currentView || currentView === 'requirement-coverage' || currentView === 'defects');

  const showFilterButton = !!currentView;

  return (
    <div className="d-flex justify-content-end gap-2 mx-2">
      <Button
        variant="outlined"
        color="primary"
        onClick={downloadPDF}
        title="Export to PDF"
        startIcon={exporting ? null : <FileDownload />}>
        {exporting ? <Spinner animation="border" size="sm" /> : 'Export'}
      </Button>

      {showJiraSelector && (
        <div style={{ width: '200px' }} title="Select linked JIRA project">
          <LinkedProjectSelector
            linkedProjects={linkedJiraProjects}
            selectedProjectId={selectedJiraProject?.linked_project_id}
            onProjectChange={handleJiraProjectChange}
          />
        </div>
      )}
      {showFilterButton && (
        <CustomIconButton
          title="Filter by date"
          color="primary"
          onClick={() => setIsFilterOpen(true)}>
          <FilterAlt />
        </CustomIconButton>
      )}
    </div>
  );
};

export default ExportButtonAndFilters;
